import React, { useEffect, useState } from "react";
import HRHeader from "../components/HRHeader";
import api from "../api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Gift, Tag, Building, Plus, Pencil, Trash2, Users, Car, Laptop, Smartphone } from "lucide-react";

const CAT_COLORS = {
  Retribuzione: "#16345c", Salute: "#c0392b", Benessere: "#009640",
  Previdenza: "#8e44ad", "Mobilità": "#0d6cb8", Convenzione: "#e67e22", Dotazioni: "#16a34a",
};
const CATEGORIES = ["Convenzione", "Retribuzione", "Salute", "Benessere", "Previdenza", "Mobilità", "Dotazioni"];
const AUDIENCE_LABEL = { all: "Tutti", dipendenti: "Dipendenti", esterni: "Esterni" };
const AUDIENCE_CLS = { all: "bg-slate-100 text-slate-600", dipendenti: "bg-blue-100 text-blue-700", esterni: "bg-orange-100 text-orange-700" };
const EMPTY = { title: "", category: "Convenzione", description: "", partner: "", discount: "", audience: "all", car_model: "", car_plate: "", car_employee_id: "", car_employee_name: "", car_insurance_expiry: "", car_revision_expiry: "", pc_model: "", pc_serial: "", pc_employee_id: "", pc_employee_name: "", pc_return_date: "", phone_model: "", phone_imei: "", phone_line: "", phone_employee_id: "", phone_employee_name: "", phone_return_date: "" };

const inputCls = "w-full border border-gray-300 rounded-sm px-3 py-2 text-[14px] outline-none focus:border-[#16345c]";
const labelCls = "block text-[12px] font-semibold text-gray-600 mb-1";

const WelfarePage = () => {
  const { isAdmin } = useAuth();
  const [benefits, setBenefits] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/benefits").then((r) => setBenefits(r.data)).catch(() => {});
  useEffect(() => {
    load();
    if (isAdmin) api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
  }, [isAdmin]);

  const save = async () => {
    if (!form.title.trim()) { toast.error("Inserisci il titolo"); return; }
    setSaving(true);
    try {
      const body = { title: form.title, category: form.category, description: form.description, partner: form.partner, discount: form.discount, audience: form.audience, car_model: form.car_model || "", car_plate: form.car_plate || "", car_employee_id: form.car_employee_id || "", car_employee_name: form.car_employee_name || "", car_insurance_expiry: form.car_insurance_expiry || "", car_revision_expiry: form.car_revision_expiry || "", pc_model: form.pc_model || "", pc_serial: form.pc_serial || "", pc_employee_id: form.pc_employee_id || "", pc_employee_name: form.pc_employee_name || "", pc_return_date: form.pc_return_date || "", phone_model: form.phone_model || "", phone_imei: form.phone_imei || "", phone_line: form.phone_line || "", phone_employee_id: form.phone_employee_id || "", phone_employee_name: form.phone_employee_name || "", phone_return_date: form.phone_return_date || "" };
      if (form._edit) { await api.put(`/benefits/${form.id}`, body); toast.success("Benefit aggiornato"); }
      else { await api.post("/benefits", body); toast.success("Benefit creato"); }
      setForm(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    try { await api.delete(`/benefits/${toDelete.id}`); toast.success("Benefit eliminato"); setToDelete(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="min-h-screen bg-[#f0f2f5]" data-testid="welfare-page">
      <HRHeader />
      <main className="max-w-[1200px] mx-auto px-4 py-8">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <h1 className="text-[26px] font-black text-[#16345c] mb-1 flex items-center gap-2"><Gift size={24} /> Welfare e Benefit</h1>
            <p className="text-[14px] text-gray-500">Convenzioni e vantaggi riservati al personale del gruppo.</p>
          </div>
          {isAdmin && (
            <button onClick={() => setForm({ ...EMPTY })} data-testid="welfare-add-btn" className="flex items-center gap-2 bg-[#16345c] hover:bg-[#0d1b2f] text-white text-[13px] font-bold px-4 py-2.5 rounded-sm transition-colors"><Plus size={16} /> Aggiungi benefit</button>
          )}
        </div>

        {benefits.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-10 text-center text-gray-400 text-[13px]">Nessun benefit disponibile.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="benefits-grid">
            {benefits.map((b) => {
              const color = CAT_COLORS[b.category] || "#16345c";
              const aud = b.audience || "all";
              return (
                <div key={b.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow flex flex-col">
                  <div className="h-1.5" style={{ backgroundColor: color }} />
                  <div className="p-5 flex-1">
                    <div className="flex items-center justify-between mb-3">
                      <span className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}1a`, color }}><Gift size={22} /></span>
                      <div className="flex items-center gap-2">
                        {b.discount && <span className="text-[15px] font-black" style={{ color }}>{b.discount}</span>}
                        {isAdmin && (
                          <div className="flex gap-1">
                            <button onClick={() => setForm({ ...b, _edit: true })} data-testid={`edit-benefit-${b.id}`} className="w-7 h-7 flex items-center justify-center rounded-sm text-[#16345c] hover:bg-[#16345c]/10 transition-colors"><Pencil size={13} /></button>
                            <button onClick={() => setToDelete(b)} data-testid={`delete-benefit-${b.id}`} className="w-7 h-7 flex items-center justify-center rounded-sm text-[#c00510] hover:bg-red-50 transition-colors"><Trash2 size={13} /></button>
                          </div>
                        )}
                      </div>
                    </div>
                    <h3 className="text-[15px] font-bold text-[#16345c] leading-snug">{b.title}</h3>
                    <p className="text-[13px] text-gray-500 leading-relaxed mt-1">{b.description}</p>
                    {(b.car_model || b.car_plate) && (
                      <div className="mt-2 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-sm px-2.5 py-1.5" data-testid={`benefit-car-${b.id}`}>
                        <Car size={15} className="text-[#0d6cb8] shrink-0" />
                        <span className="text-[12.5px] text-gray-700">
                          {b.car_model && <b className="text-[#16345c]">{b.car_model}</b>}
                          {b.car_model && b.car_plate && " · "}
                          {b.car_plate && <span className="font-mono uppercase tracking-wide">{b.car_plate}</span>}
                          {b.car_employee_name && <span className="text-gray-500"> — Assegnato a <b className="text-[#16345c]">{b.car_employee_name}</b></span>}
                        </span>
                      </div>
                    )}
                    {(b.pc_model || b.pc_serial) && (
                      <div className="mt-2 flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-sm px-2.5 py-1.5" data-testid={`benefit-pc-${b.id}`}>
                        <Laptop size={15} className="text-emerald-600 shrink-0" />
                        <span className="text-[12.5px] text-gray-700">
                          {b.pc_model && <b className="text-[#16345c]">{b.pc_model}</b>}
                          {b.pc_serial && <span className="text-gray-500"> · Matr. <span className="font-mono uppercase">{b.pc_serial}</span></span>}
                          {b.pc_employee_name && <span className="text-gray-500"> — Assegnato a <b className="text-[#16345c]">{b.pc_employee_name}</b></span>}
                        </span>
                      </div>
                    )}
                    {(b.phone_model || b.phone_imei || b.phone_line) && (
                      <div className="mt-2 flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-sm px-2.5 py-1.5" data-testid={`benefit-phone-${b.id}`}>
                        <Smartphone size={15} className="text-indigo-600 shrink-0" />
                        <span className="text-[12.5px] text-gray-700">
                          {b.phone_model && <b className="text-[#16345c]">{b.phone_model}</b>}
                          {b.phone_line && <span className="text-gray-500"> · Linea <span className="font-mono">{b.phone_line}</span></span>}
                          {b.phone_imei && <span className="text-gray-500"> · IMEI <span className="font-mono">{b.phone_imei}</span></span>}
                          {b.phone_employee_name && <span className="text-gray-500"> — Assegnato a <b className="text-[#16345c]">{b.phone_employee_name}</b></span>}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-[12px] text-gray-500 mt-3 pt-3 border-t border-gray-100 flex-wrap">
                      <span className="flex items-center gap-1"><Tag size={12} /> {b.category}</span>
                      {b.partner && <span className="flex items-center gap-1"><Building size={12} /> {b.partner}</span>}
                      <span className={`flex items-center gap-1 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full ${AUDIENCE_CLS[aud]}`}><Users size={11} /> {AUDIENCE_LABEL[aud]}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Admin: create/edit dialog */}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-[520px] max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{form?._edit ? "Modifica benefit" : "Nuovo benefit"}</DialogTitle>
            <DialogDescription className="sr-only">Modulo per creare o modificare un benefit welfare.</DialogDescription>
          </DialogHeader>
          {form && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className={labelCls}>Titolo *</label>
                <input value={form.title} data-testid="benefit-title" onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Categoria</label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className={labelCls}>Destinatari</label>
                <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
                  <SelectTrigger data-testid="benefit-audience"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti (dipendenti + esterni)</SelectItem>
                    <SelectItem value="dipendenti">Solo dipendenti</SelectItem>
                    <SelectItem value="esterni">Solo collaboratori esterni</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className={labelCls}>Partner / convenzione</label>
                <input value={form.partner} onChange={(e) => setForm({ ...form, partner: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Sconto / valore</label>
                <input value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} placeholder="es. -20%" className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Descrizione</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className={`${inputCls} resize-none`} />
              </div>
              <div className="sm:col-span-2 border-t border-gray-100 pt-3">
                <div className="flex items-center gap-2 text-[12.5px] font-bold text-[#16345c] mb-2"><Car size={15} className="text-[#0d6cb8]" /> Auto aziendale (opzionale)</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Modello</label>
                    <input value={form.car_model || ""} data-testid="benefit-car-model" onChange={(e) => setForm({ ...form, car_model: e.target.value })} placeholder="es. Fiat 500 Hybrid" className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Targa</label>
                    <input value={form.car_plate || ""} data-testid="benefit-car-plate" onChange={(e) => setForm({ ...form, car_plate: e.target.value.toUpperCase() })} placeholder="es. AB123CD" className={`${inputCls} uppercase`} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Assegnatario (dipendente)</label>
                    <Select value={form.car_employee_id || "none"} onValueChange={(v) => {
                      const emp = employees.find((e) => e.id === v);
                      setForm({ ...form, car_employee_id: v === "none" ? "" : v, car_employee_name: emp ? emp.name : "" });
                    }}>
                      <SelectTrigger data-testid="benefit-car-employee"><SelectValue placeholder="Non assegnato" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Non assegnato</SelectItem>
                        {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} ({e.matricola})</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className={labelCls}>Scadenza assicurazione</label>
                    <input type="date" value={form.car_insurance_expiry || ""} data-testid="benefit-car-insurance" onChange={(e) => setForm({ ...form, car_insurance_expiry: e.target.value })} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Scadenza revisione</label>
                    <input type="date" value={form.car_revision_expiry || ""} data-testid="benefit-car-revision" onChange={(e) => setForm({ ...form, car_revision_expiry: e.target.value })} className={inputCls} />
                  </div>
                </div>
              </div>

              <div className="sm:col-span-2 border-t border-gray-100 pt-3">
                <div className="flex items-center gap-2 text-[12.5px] font-bold text-[#16345c] mb-2"><Laptop size={15} className="text-emerald-600" /> Personal computer (opzionale)</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Modello</label>
                    <input value={form.pc_model || ""} data-testid="benefit-pc-model" onChange={(e) => setForm({ ...form, pc_model: e.target.value })} placeholder="es. PC portatile Dell Latitude" className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Numero di matricola / seriale</label>
                    <input value={form.pc_serial || ""} data-testid="benefit-pc-serial" onChange={(e) => setForm({ ...form, pc_serial: e.target.value.toUpperCase() })} placeholder="es. SN-8842AB" className={`${inputCls} uppercase`} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Assegnatario (dipendente)</label>
                    <Select value={form.pc_employee_id || "none"} onValueChange={(v) => {
                      const emp = employees.find((e) => e.id === v);
                      setForm({ ...form, pc_employee_id: v === "none" ? "" : v, pc_employee_name: emp ? emp.name : "" });
                    }}>
                      <SelectTrigger data-testid="benefit-pc-employee"><SelectValue placeholder="Non assegnato" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Non assegnato</SelectItem>
                        {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} ({e.matricola})</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Data riconsegna / fine assegnazione</label>
                    <input type="date" value={form.pc_return_date || ""} data-testid="benefit-pc-return" onChange={(e) => setForm({ ...form, pc_return_date: e.target.value })} className={inputCls} />
                  </div>
                </div>
              </div>

              <div className="sm:col-span-2 border-t border-gray-100 pt-3">
                <div className="flex items-center gap-2 text-[12.5px] font-bold text-[#16345c] mb-2"><Smartphone size={15} className="text-indigo-600" /> Telefono aziendale (opzionale)</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Modello</label>
                    <input value={form.phone_model || ""} data-testid="benefit-phone-model" onChange={(e) => setForm({ ...form, phone_model: e.target.value })} placeholder="es. iPhone 14" className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Numero linea telefonica</label>
                    <input value={form.phone_line || ""} data-testid="benefit-phone-line" onChange={(e) => setForm({ ...form, phone_line: e.target.value })} placeholder="es. +39 340 1234567" className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>IMEI</label>
                    <input value={form.phone_imei || ""} data-testid="benefit-phone-imei" onChange={(e) => setForm({ ...form, phone_imei: e.target.value })} placeholder="es. 356938035643809" className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Assegnatario (dipendente)</label>
                    <Select value={form.phone_employee_id || "none"} onValueChange={(v) => {
                      const emp = employees.find((e) => e.id === v);
                      setForm({ ...form, phone_employee_id: v === "none" ? "" : v, phone_employee_name: emp ? emp.name : "" });
                    }}>
                      <SelectTrigger data-testid="benefit-phone-employee"><SelectValue placeholder="Non assegnato" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Non assegnato</SelectItem>
                        {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} ({e.matricola})</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className={labelCls}>Data riconsegna / fine assegnazione</label>
                    <input type="date" value={form.phone_return_date || ""} data-testid="benefit-phone-return" onChange={(e) => setForm({ ...form, phone_return_date: e.target.value })} className={inputCls} />
                  </div>
                </div>
                <p className="text-[11px] text-gray-400 mt-1.5">Assegnando una linea telefonica a un dipendente, il numero viene aggiornato automaticamente nella Rubrica aziendale.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setForm(null)} className="px-4 py-2 text-[13px] font-semibold text-gray-600 hover:bg-gray-100 rounded-sm transition-colors">Annulla</button>
            <button onClick={save} disabled={saving} data-testid="benefit-save" className="px-4 py-2 text-[13px] font-bold text-white bg-[#16345c] hover:bg-[#0d1b2f] rounded-sm transition-colors disabled:opacity-60">{saving ? "Salvataggio..." : "Salva"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin: delete confirm */}
      <Dialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <DialogContent className="max-w-[420px]">
          <DialogHeader><DialogTitle>Eliminare il benefit?</DialogTitle></DialogHeader>
          <DialogDescription className="text-[14px] text-gray-600">Stai per eliminare <b>{toDelete?.title}</b>. Operazione non reversibile.</DialogDescription>
          <DialogFooter>
            <button onClick={() => setToDelete(null)} data-testid="benefit-delete-cancel" className="px-4 py-2 text-[13px] font-semibold text-gray-600 hover:bg-gray-100 rounded-sm transition-colors">Annulla</button>
            <button onClick={remove} data-testid="benefit-delete-confirm" className="px-4 py-2 text-[13px] font-bold text-white bg-[#e30613] hover:bg-[#c00510] rounded-sm transition-colors">Elimina</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WelfarePage;
